import { ChangeEvent, useEffect, useState } from "react"

import styles from "./PlaybackScrubBar.module.css"

export interface PlaybackScrubBarProps {
  currentTime: number
  onCurrentTimeChange: (newCurrentTime: number) => void
  duration: number
}

export const PlaybackScrubBar = ({
  currentTime,
  onCurrentTimeChange,
  duration,
}: PlaybackScrubBarProps) => {
  const [timeToDisplayWhileChanging, setTimeToDisplayWhileChanging] =
    useState<number | null>(null)

  useEffect(() => {
    if (timeToDisplayWhileChanging !== null) {
      setTimeToDisplayWhileChanging(null)
    }
  }, [currentTime])

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCurrentTimeChange(event.target.valueAsNumber)
    setTimeToDisplayWhileChanging(event.target.valueAsNumber)
  }

  const timeToDisplay = timeToDisplayWhileChanging ?? currentTime
  const percentElapsed = (timeToDisplay / duration) * 100
  const THUMB_RADIUS_PX = 4

  // The browser places the slider's 'thumb' such that the EDGE of the thumb (not the CENTER) matches the percent elapsed.
  // This adds a margin of error so our custom gradient is placed properly & never 'peeks' out from under the thumb.
  const percentElapsedString =
    percentElapsed < 50
      ? `calc(${percentElapsed}% + ${((50 - percentElapsed) / 50) * THUMB_RADIUS_PX}px)`
      : `calc(${percentElapsed}% - ${((percentElapsed - 50) / 50) * THUMB_RADIUS_PX}px)`

  return (
    <div className={styles.scrubBarContainer}>
      <input
        className={styles.scrubBarInput}
        type="range"
        value={timeToDisplay}
        min={0}
        max={duration}
        step={0.01}
        onChange={onChange}
        // Custom gradient, needed since we are using `appearance: none` to override the browser's native rangeInput styles.
        style={{
          background: `linear-gradient(to right,  rgb(var(--fg-color-inverse)) ${percentElapsedString}, rgba(var(--fg-color-inverse), 30%) ${percentElapsedString})`,
        }}
      />
    </div>
  )
}
